import { formatBps } from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    DateTime,
    PageHeader,
    StatusBadge,
    type ListColumnDef,
    type ListViewPreset,
} from '@bemor/web-kit/ui';
import { Link } from 'react-router-dom';

import type { RiskMonitoringRow } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { ADMIN_STATUSES } from '@/lib/statuses';

const PATH = '/staff/risk/monitoring';
const PAGE_KEY = 'risk-monitoring';

/** BM-125 slice 3: the merchants on Action first (the contract's default sort is the worst state first). */
export const RISK_MONITORING_PRESETS: ListViewPreset[] = [
    { key: 'all', name: 'All', filters: {} },
    {
        key: 'attention',
        name: 'Watch or Action',
        filters: { state: ['watch', 'action'] },
    },
    {
        key: 'measures',
        name: 'Under a measure',
        filters: { measures: ['true'] },
    },
];

export const riskMonitoringPrefetch = adminListPrefetch(PAGE_KEY, PATH, {
    presets: RISK_MONITORING_PRESETS,
});

const monitoringFilters = filterDefs(PATH, { state: 'State' });

export const monitoringHref = (merchantId: string) =>
    `/risk/monitoring/${merchantId}`;

const bps = (v: unknown) => (typeof v === 'number' ? formatBps(v) : '—');

/** BM-125 decision 16: Risk → Monitoring, each merchant's ratios against the PT rules, its measures and its case. */
export function RiskMonitoringPage() {
    const columns: ListColumnDef<RiskMonitoringRow>[] = [
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            sortKey: 'merchant_id',
            csv: (r) => r.merchant_id,
            boardCard: true,
            locked: 'start',
            cell: (r) => (
                <Link
                    to={monitoringHref(r.merchant_id)}
                    className="hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {r.merchant_name}
                </Link>
            ),
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            csv: (r) => r.state,
            boardCard: true,
            cell: (r) => (
                <StatusBadge
                    status={r.state}
                    domain={ADMIN_STATUSES.riskMonitoring}
                />
            ),
        },
        {
            key: 'rating',
            label: 'Rating',
            csv: (r) => r.rating ?? '',
            cell: (r) => (r.rating ? <Badge>{r.rating}</Badge> : '—'),
        },
        {
            key: 'chargeback_ratio_bps',
            aliases: ['chargebacks'],
            label: 'Chargebacks (month)',
            sortKey: 'chargeback_ratio_bps',
            csv: (r) => String(r.month?.figures.chargeback_ratio_bps ?? ''),
            cell: (r) => bps(r.month?.figures.chargeback_ratio_bps),
            noTruncate: true,
        },
        {
            key: 'vamp_ratio_bps',
            aliases: ['vamp'],
            label: 'VAMP (month)',
            sortKey: 'vamp_ratio_bps',
            csv: (r) => String(r.month?.figures.vamp_ratio_bps ?? ''),
            cell: (r) => bps(r.month?.figures.vamp_ratio_bps),
            noTruncate: true,
        },
        {
            key: 'decline_ratio_bps',
            aliases: ['declines'],
            label: 'Declines (day)',
            sortKey: 'decline_ratio_bps',
            csv: (r) => String(r.day?.figures.decline_ratio_bps ?? ''),
            cell: (r) => bps(r.day?.figures.decline_ratio_bps),
            noTruncate: true,
        },
        {
            key: 'active_measures',
            aliases: ['measures'],
            label: 'Measures',
            csv: (r) => r.active_measures.join(' '),
            cell: (r) =>
                r.active_measures.length === 0 ? (
                    '—'
                ) : (
                    <span className="flex flex-wrap gap-1">
                        {r.active_measures.map((k) => (
                            <StatusBadge
                                key={k}
                                status={k}
                                domain={ADMIN_STATUSES.riskMeasureKind}
                            />
                        ))}
                    </span>
                ),
        },
        {
            key: 'computed_at',
            aliases: ['run'],
            label: 'Last run',
            kind: 'datetime',
            sortKey: 'computed_at',
            csv: (r) => r.computed_at ?? '',
            cell: (r) => <DateTime value={r.computed_at} empty="Not run yet" />,
            noTruncate: true,
        },
    ];
    const { state, query } = useAdminList<RiskMonitoringRow>(PAGE_KEY, PATH, {
        columns,
        filters: monitoringFilters,
        presets: RISK_MONITORING_PRESETS,
    });
    return (
        <div>
            <PageHeader
                title="Monitoring"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            Each merchant&apos;s ratios against the monitoring
                            rules, computed every night and on the first of the
                            month. Action pauses payouts until staff lift the
                            pause.
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="merchants"
                state={state}
                query={query}
                rowKey={(r) => r.merchant_id}
                controls={{
                    state: { domain: ADMIN_STATUSES.riskMonitoring },
                }}
            />
        </div>
    );
}
